import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'custom-table',
    templateUrl: './custom-table.component.html',
    styleUrls: ['./custom-table.component.scss']
})
export class CustomTableComponent implements OnInit, OnChanges {
    @Input() riskLevels: any[] = [];
    @Output() riskLevelsChange = new EventEmitter<any[]>();

    constructor(private translate: TranslateService) { }

    ngOnInit(): void {
        this.updateRiskLabels();

        // Subscribe to language changes
        this.translate.onLangChange.subscribe(() => {
            this.updateRiskLabels();
        });
    }

    updateRiskLabels(): void {
        if (this.riskLevels && this.riskLevels.length > 0) {
            this.translate.get([
                'new_entries.high_risk',
                'new_entries.medium_risk',
                'new_entries.low_risk'
            ]).subscribe(translations => {
                this.riskLevels.forEach((risk, index) => {
                    if (index === 0) {
                        risk.label = translations['new_entries.high_risk'];
                    } else if (index === 1) {
                        risk.label = translations['new_entries.medium_risk'];
                    } else if (index === 2) {
                        risk.label = translations['new_entries.low_risk'];
                    }
                });
            });
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['riskLevels'] && !changes['riskLevels'].firstChange) {
            this.updateRiskLabels();
        }
    }

    onInputChange(index: number, field: 'from' | 'to', value: number) {
        const updated = [...this.riskLevels];
        const currentRisk = updated[index];

        // Handle NaN or invalid input
        if (isNaN(value) || value === null || value === undefined) {
            value = 0;
        }

        // Ensure value is an integer
        value = Math.floor(value);

        // Validate input bounds (0-100)
        if (value < 0) value = 0;
        if (value > 100) value = 100;

        // Apply specific field constraints
        if (field === 'from') {
            // From value cannot exceed its own To - 1
            const maxFromValue = Math.max(0, currentRisk.to - 1);
            if (value > maxFromValue) {
                value = maxFromValue;
            }
        }

        // Apply additional constraints based on risk level relationships
        if (index === 1 && field === 'to' && updated.length > 0) {
            // Medium risk "To" cannot exceed High risk "To" - 1
            const maxAllowed = Math.max(1, updated[0].to - 1);
            if (value > maxAllowed) {
                value = maxAllowed;
            }
        } else if (index === 2 && field === 'to' && updated.length > 1) {
            // Low risk "To" cannot exceed Medium risk "To" - 1
            const maxAllowed = Math.max(1, updated[1].to - 1);
            if (value > maxAllowed) {
                value = maxAllowed;
            }
        }

        // Apply the validated change
        updated[index][field] = value;

        // Additional validation: ensure from < to for current risk level
        if (field === 'from' && updated[index].from >= updated[index].to) {
            updated[index].from = Math.max(0, updated[index].to - 1);
        } else if (field === 'to' && updated[index].to <= updated[index].from) {
            updated[index].to = Math.min(this.getMaxToValue(index), updated[index].from + 1);
        }

        // Forward sync - when changing "from" of higher risk level
        if (index === 0 && field === 'from' && updated.length > 1) {
            updated[1].to = updated[index].from; // High → Medium
            // Ensure medium risk maintains from < to
            if (updated[1].from >= updated[1].to) {
                updated[1].from = Math.max(0, updated[1].to - 1);
            }
        } else if (index === 1 && field === 'from' && updated.length > 2) {
            updated[2].to = updated[index].from; // Medium → Low
            // Ensure low risk maintains from < to
            if (updated[2].from >= updated[2].to) {
                updated[2].from = Math.max(0, updated[2].to - 1);
            }
        }

        // Backward sync - when changing "to" of lower risk level
        if (index === 1 && field === 'to' && updated.length > 0) {
            updated[0].from = updated[index].to; // Medium → High
            // Ensure high risk maintains from < to
            if (updated[0].from >= updated[0].to) {
                updated[0].from = Math.max(0, updated[0].to - 1);
            }
        } else if (index === 2 && field === 'to' && updated.length > 1) {
            updated[1].from = updated[index].to; // Low → Medium
            // Ensure medium risk maintains from < to
            if (updated[1].from >= updated[1].to) {
                updated[1].from = Math.max(0, updated[1].to - 1);
            }
        }

        // Final validation pass: ensure all risk levels maintain constraints
        updated.forEach((risk, i) => {
            // Ensure from < to
            if (risk.from >= risk.to) {
                if (risk.to > 0) {
                    risk.from = Math.max(0, risk.to - 1);
                } else {
                    risk.to = Math.min(this.getMaxToValue(i), risk.from + 1);
                }
            }

            // Ensure values are within global bounds
            risk.from = Math.max(0, Math.min(risk.from, this.getMaxFromValue(i)));
            risk.to = Math.max(1, Math.min(risk.to, this.getMaxToValue(i)));
        });

        // Update the model to reflect corrected values in UI
        this.riskLevels = updated;
        this.riskLevelsChange.emit(updated);
        console.log(updated, "updated risk levels");
    }

    getMaxFromValue(index: number): number {
        const currentRisk = this.riskLevels[index];
        return currentRisk && currentRisk.to > 0 ? currentRisk.to - 1 : 99;
    }

    getMinToValue(index: number): number {
        const currentRisk = this.riskLevels[index];
        return currentRisk && currentRisk.from < 100 ? currentRisk.from + 1 : 1;
    }

    getMaxToValue(index: number): number {
        // For medium risk (index 1), max "To" is High risk "To" - 1
        if (index === 1 && this.riskLevels.length > 0) {
            return Math.max(1, this.riskLevels[0].to - 1);
        }
        // For low risk (index 2), max "To" is Medium risk "To" - 1
        if (index === 2 && this.riskLevels.length > 1) {
            return Math.max(1, this.riskLevels[1].to - 1);
        }
        // For high risk (index 0), max is 100
        return 100;
    }
}
